<template>
  <div :class="['ui-callout', `ui-callout--${tone}`, `ui-callout--${size}`]" :role="role">
    <span v-if="$slots['icon']" class="ui-callout__icon" aria-hidden="true"
      ><slot name="icon"
    /></span>
    <div class="ui-callout__text">
      <b v-if="title || $slots['title']" class="ui-callout__title"
        ><slot name="title">{{ title }}</slot></b
      >
      <span v-if="$slots['default']" class="ui-callout__body"><slot /></span>
    </div>
    <div v-if="$slots['action']" class="ui-callout__action"><slot name="action" /></div>
  </div>
</template>

<script setup lang="ts">
/**
 * A finding in a tinted strip: what it is in bold, why in a sentence, and
 * where to go from it. `sm` is the small note under a figure ("Fiabilisé:
 * 99,6 % des enregistrements conformes").
 *
 * `role="status"` is for one that appears in answer to something, so it
 * is announced; a strip that is simply part of the page needs none.
 */
withDefaults(
  defineProps<{
    tone?: 'cyan' | 'blue' | 'violet' | 'neutral' | 'warning' | 'danger'
    size?: 'sm' | 'md'
    title?: string
    role?: 'status' | 'alert' | undefined
  }>(),
  { tone: 'blue', size: 'md', title: '', role: undefined },
)
</script>

<style scoped>
.ui-callout {
  --callout-bg: var(--color-primary-soft);
  --callout-mark: var(--color-primary);

  display: flex;
  align-items: center;
  gap: 14px;
  border-radius: var(--radius-card);
  background: var(--callout-bg);
  color: var(--color-text);
}

.ui-callout--md {
  padding: 12px 14px;
}

.ui-callout--sm {
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border-radius: var(--radius-lg);
}

.ui-callout--cyan {
  --callout-bg: var(--color-cyan-wash);
  --callout-mark: var(--color-cyan-deep);
}

.ui-callout--violet {
  --callout-bg: var(--color-violet-wash);
  --callout-mark: var(--color-violet);
}

.ui-callout--neutral {
  --callout-bg: var(--color-surface-sunken);
  --callout-mark: var(--color-text-muted);
}

.ui-callout--warning {
  --callout-bg: var(--color-warning-soft);
  --callout-mark: var(--color-warning);
}

.ui-callout--danger {
  --callout-bg: var(--color-danger-soft);
  --callout-mark: var(--color-danger);
}

.ui-callout__icon {
  display: grid;
  flex: none;
  place-items: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--callout-mark);
}

.ui-callout--sm .ui-callout__icon {
  width: 22px;
  height: 22px;
}

.ui-callout__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}

.ui-callout__title {
  font-size: 13.5px;
  font-weight: var(--weight-bold);
  color: var(--color-text);
}

.ui-callout--sm .ui-callout__title {
  font-size: var(--text-sm);
}

.ui-callout__body {
  font-size: var(--text-sm);
  line-height: 1.45;
  color: var(--color-text-muted);
}

.ui-callout__action {
  display: flex;
  flex: none;
  align-items: center;
  gap: var(--space-2);
}
</style>
